import api from "@/components/customAxios/Axios";
import { useToast } from "@/components/toast";
import { useLoader } from "@/context/loaderContext";
import { useState } from "react";

import Modal from "react-modal";
import { IoClose } from "react-icons/io5";
import { Link } from "react-router-dom";

Modal.setAppElement("#root");

const customStyles = {
	overlay: {
		backgroundColor: "rgba(0, 0, 0, 0)", // fully transparent; change to rgba(0,0,0,0.5) for dim without blur
	},
	content: {
		top: "50%",
		left: "50%",
		right: "auto",
		bottom: "auto",
		marginRight: "-50%",
		transform: "translate(-50%, -50%)",
		zIndex: 1000,
		borderRadius: "10px",

		width: "100%", // Adjust modal width for responsiveness
		maxWidth: "900px", // Max width for larger screens
		maxHeight: "90vh", // Limit height for smaller screens
	},
};
type ExtractedInfoDetails = {
	address: string;
	behaviour_skills: string;
	candidate_name: string;
	date_of_birth: string;
	email_id: string;
	github_id: string;
	job_details: {
		job_description: string;
		role: string;
		job_id: string;
	};
	linkedin_id: string;
	nationality: string;
	phone_number: string;
	tech_skills: string[];
	total_experience: string;
};

type Data = {
	extracted_info_details: ExtractedInfoDetails;
};
const CandidateDetailsModal = ({
	candidateId,
	candidateName,
}: {
	candidateId: string;
	candidateName: string;
}) => {
	const [modalIsOpen, setIsOpen] = useState(false);
	const { setLoading } = useLoader();
	const toast = useToast();
	const [candidateDetails, setCandidateDetails] = useState<Data>();
	const [showFullDescription, setShowFullDescription] = useState(false);
	const getCandidateDetails = async () => {
		setLoading(true);
		try {
			const res = await api.get(`/dashboard/candidate_profile_details`, {
				params: {
					candidate_id: candidateId,
				},
			});

			setCandidateDetails(res.data);
		} catch (err: any) {
			setLoading(false);
			toast.error({
				type: "background",
				duration: 5000,
				status: "error",
				title: "Error Fetching Details",
				description: err.response?.data?.error || err.response?.data?.message || "Failed to fetch candidate details. Please try again.",
				open: true,
			});
		} finally {
			setLoading(false);
		}
	};

	function openModal() {
		getCandidateDetails();
		setIsOpen(true);
	}

	function closeModal() {
		setIsOpen(false);
	}

	return (
		<div>
			<button
				onClick={openModal}
				className="text-left text-[#438437] font-medium underline focus:outline-none mt-2 w-full md:w-auto"
			>
				{candidateName}
			</button>
			<Modal
				isOpen={modalIsOpen}
				onRequestClose={closeModal}
				style={customStyles}
				contentLabel="Candidate Information Modal"
			>
				<button
					onClick={closeModal}
					className="absolute top-0 right-2 p-2 rounded-full bg-red-500 text-white hover:bg-red-400 focus:ring-2 focus:ring-red-300"
					aria-label="Close Modal"
				>
					<IoClose size={20} />
				</button>
				<div className=" bg-white shadow-lg rounded-lg p-6 border border-gray-200">
					{/* Close Button */}

					{/* Candidate Header */}
					<div className="border-b pb-4 mb-4 flex justify-between">
						<h2 className="text-2xl font-bold text-gray-800 mb-2">
							{candidateDetails?.extracted_info_details.candidate_name}
						</h2>
						<div className="text-sm text-gray-600">
							<p>
								<strong>Email:</strong>{" "}
								{candidateDetails?.extracted_info_details.email_id}
							</p>
							<p>
								<strong>Phone:</strong>{" "}
								{candidateDetails?.extracted_info_details.phone_number}
							</p>
							<p>
								<strong>Address:</strong>{" "}
								{candidateDetails?.extracted_info_details.address}
							</p>
						</div>
					</div>

					{/* Technical Skills */}
					<div className="mb-6">
						<div className="flex justify-between">
							<h3 className="text-lg font-semibold text-gray-800 mb-2">
								Job Details
							</h3>
							<div className=" text-center">
								<Link
									to={`/respective-dashboard/${candidateDetails?.extracted_info_details.job_details.job_id}`}
									className="text-indigo-500 hover:underline"
								>
									See Job Details
								</Link>
							</div>
						</div>
						<p className=" text-gray-700">
							<strong className="mr-1">Role:</strong>{" "}
							{candidateDetails?.extracted_info_details.job_details.role ||
								"N/A"}
						</p>

						<p className="my-3 text-gray-700">
							<strong className="mr-1">Job Description:</strong>{" "}
							{candidateDetails?.extracted_info_details.job_details
								.job_description ? (
								<>
									{showFullDescription
										? candidateDetails.extracted_info_details.job_details
											.job_description
										: `${candidateDetails.extracted_info_details.job_details.job_description.slice(
											0,
											100
										)}...`}
									<button
										onClick={() => setShowFullDescription(!showFullDescription)}
										className="ml-2 text-indigo-500 hover:underline focus:outline-none"
									>
										{showFullDescription ? "Show Less" : "Read More"}
									</button>
								</>
							) : (
								"N/A"
							)}
						</p>
					</div>

					{/* Technical Skills */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-gray-800 mb-3">
							Technical Skills
						</h3>
						<div className="flex flex-wrap gap-2">
							{candidateDetails?.extracted_info_details.tech_skills.map(
								(skill, index) => (
									<span
										key={index}
										className="bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm font-medium"
									>
										{skill}
									</span>
								)
							)}
						</div>
					</div>

					{/* Behavioral Skills */}
					<div className="mb-6">
						<h3 className="text-lg font-semibold text-gray-800 mb-3">
							Behavioral Skills
						</h3>
						{Array.isArray(
							candidateDetails?.extracted_info_details?.behaviour_skills
						) ? (
							<div className="flex flex-wrap gap-2">
								{candidateDetails?.extracted_info_details.behaviour_skills.map(
									(skill, index) => (
										<span
											key={index}
											className="bg-green-100 text-green-800 rounded-full px-3 py-1 text-sm font-medium"
										>
											{skill}
										</span>
									)
								)}
							</div>
						) : (
							<p className="text-sm text-gray-500">
								No behavioral skills listed.
							</p>
						)}
					</div>

					{/* Additional Information */}
					<div>
						<h3 className="text-lg font-semibold text-gray-800 mb-3">
							Additional Information
						</h3>
						<ul className="space-y-2">
							<li className="text-sm text-gray-700">
								<strong>Date of Birth:</strong>{" "}
								{candidateDetails?.extracted_info_details.date_of_birth ||
									"N/A"}
							</li>
							<li className="text-sm text-gray-700">
								<strong>Nationality:</strong>{" "}
								{candidateDetails?.extracted_info_details.nationality || "N/A"}
							</li>
							<li className="text-sm text-gray-700">
								<strong>LinkedIn:</strong>{" "}
								<a
									href={`https://${candidateDetails?.extracted_info_details.linkedin_id}`}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-500 hover:underline"
								>
									{candidateDetails?.extracted_info_details.linkedin_id}
								</a>
							</li>
							<li className="text-sm text-gray-700">
								<strong>GitHub:</strong>{" "}
								<a
									href={`https://${candidateDetails?.extracted_info_details.github_id}`}
									target="_blank"
									rel="noopener noreferrer"
									className="text-blue-500 hover:underline"
								>
									{candidateDetails?.extracted_info_details.github_id}
								</a>
							</li>
							<li className="text-sm text-gray-700">
								<strong>Total Experience:</strong>{" "}
								{candidateDetails?.extracted_info_details.total_experience}
							</li>
						</ul>
					</div>

					{/* Job Details Link */}
				</div>
			</Modal>
		</div>
	);
};

export default CandidateDetailsModal;
